import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import i18n from '../i18n/i18n';
import apiClient from '../utils/apiClient';
import { handleAuthError } from '../utils/authErrorHandler';
import './BillingPage.css';

const BillingPage = ({ user, onBackToDashboard }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [billingData, setBillingData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [error, setError] = useState(null);

  // Gérer le style du body au montage/démontage du composant
  useEffect(() => {
    // Sauvegarder les styles originaux
    const originalOverflow = document.body.style.overflow;
    const originalMargin = document.body.style.margin;
    const originalPadding = document.body.style.padding;
    
    // Appliquer les styles pour la page de billing
    document.body.style.overflow = 'hidden';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    
    // Nettoyer au démontage
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.margin = originalMargin;
      document.body.style.padding = originalPadding;
    };
  }, []);

  useEffect(() => {
    loadBillingData();
  }, []);

  const loadBillingData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Charger les données de licence et usage en parallèle
      const [licenseResponse, usageResponse, historyResponse] = await Promise.all([
        apiClient.get('/api/license/my-license'),
        apiClient.get('/api/usage/current'),
        apiClient.get('/api/billing/history').catch(() => ({ data: [] })) // Fallback si pas d'historique
      ]);

      const license = licenseResponse.data;
      const usage = usageResponse.data;
      const history = historyResponse.data;

      setBillingData({
        license: license.license || {},
        usage: usage.usage || {},
        limits: license.limits || {},
        alerts: usage.alerts || []
      });

      setPaymentHistory(history.payments || []);
      setInvoices(history.invoices || []);

    } catch (error) {
      console.error('❌ Erreur chargement billing:', error);
      if (handleAuthError(error)) return;
      setError(t('billingPage.errors.loadingData'));
    } finally {
      setLoading(false);
    }
  };

  const getPlanDisplayName = (plan) => {
    const planNames = {
      free: t('billingPage.plans.free'),
      starter: t('billingPage.plans.starter'),
      professional: t('billingPage.plans.professional'),
      enterprise: t('billingPage.plans.enterprise')
    };
    return planNames[plan] || plan;
  };

  const getPlanFeatures = (plan) => {
    const features = {
      free: [
        { key: 'nodes', value: '50 nœuds' },
        { key: 'apiCalls', value: '500 API calls' },
        { key: 'storage', value: '0.5 GB stockage' },
        { key: 'users', value: '5 utilisateurs' },
        { key: 'support', value: 'Support communautaire' }
      ],
      starter: [
        { key: 'nodes', value: '100 nœuds' },
        { key: 'apiCalls', value: '1 000 API calls' },
        { key: 'storage', value: '1 GB stockage' },
        { key: 'users', value: '5 utilisateurs' },
        { key: 'support', value: 'Support email' }
      ],
      professional: [
        { key: 'nodes', value: '500 nœuds' },
        { key: 'apiCalls', value: '5 000 API calls' },
        { key: 'storage', value: '10 GB stockage' },
        { key: 'users', value: '25 utilisateurs' },
        { key: 'support', value: 'Support prioritaire' }
      ],
      enterprise: [
        { key: 'nodes', value: '1 000 nœuds' },
        { key: 'apiCalls', value: '10 000 API calls' },
        { key: 'storage', value: '50 GB stockage' },
        { key: 'users', value: '100 utilisateurs' },
        { key: 'support', value: 'Support dédié' }
      ]
    };
    return features[plan] || features.free;
  };

  const formatUsagePercentage = (used, limit) => {
    if (!limit || limit === 0) return 0;
    return Math.round((used / limit) * 100);
  };

  const getUsageStatus = (percentage) => {
    if (percentage >= 90) return 'critical';
    if (percentage >= 80) return 'warning';
    return 'normal';
  };

  const handleUpgradePlan = () => {
    // TODO: Implémenter la vraie intégration Stripe
    toast.info(t('billingPage.upgrade.comingSoon'));
  };

  const handleDownloadInvoice = async (invoiceId) => {
    try {
      // Afficher un message de chargement
      console.log(t('billingPage.invoice.downloading'));
      
      // Télécharger la facture PDF avec la langue actuelle
      const response = await apiClient.get(`/api/billing/invoice/${invoiceId}/download?lang=${i18n.language}`, {
        responseType: 'blob' // Important pour les fichiers PDF
      });
      
      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extraire le nom du fichier depuis les headers ou utiliser un nom par défaut
      const contentDisposition = response.headers['content-disposition'];
      let filename = `facture-${invoiceId}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      console.log(t('billingPage.invoice.downloadReady'));
      
    } catch (error) {
      console.error('❌ Erreur téléchargement facture:', error);
      toast.info(t('billingPage.invoice.error'));
    }
  };

  if (loading) {
    return (
      <div className="billing-page">
        <div className="billing-header">
          <button 
            id="billing-back-button"
            onClick={onBackToDashboard} 
            className="billing-back-button"
          >
            ← {t('common.back')}
          </button>
          <h1>💳 {t('billingPage.title')}</h1>
        </div>
        <div className="billing-loading">
          <div className="loading-spinner"></div>
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="billing-page">
        <div className="billing-header">
          <button 
            id="billing-back-button-error"
            onClick={onBackToDashboard} 
            className="billing-back-button"
          >
            ← {t('common.back')}
          </button>
          <h1>💳 {t('billingPage.title')}</h1>
        </div>
        <div className="billing-error">
          <div className="error-icon">⚠️</div>
          <h3>{t('billingPage.errors.title')}</h3>
          <p>{error}</p>
          <button 
            id="billing-retry-button"
            onClick={loadBillingData} 
            className="billing-retry-button"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  const { license, usage, limits, alerts } = billingData;

  return (
    <div className="billing-page">
      <div className="billing-header">
        <button 
          id="billing-main-back-button"
          onClick={onBackToDashboard} 
          className="billing-back-button"
        >
          ← {t('common.back')}
        </button>
        <h1>💳 {t('billingPage.title')}</h1>
        <p className="billing-subtitle">{t('billingPage.subtitle')}</p>
      </div>

      {/* Alertes globales */}
      {alerts && alerts.length > 0 && (
        <div className="billing-alerts">
          {alerts.map((alert, index) => (
            <div key={index} className={`billing-alert ${alert.level}`}>
              <span className="alert-icon">
                {alert.level === 'critical' ? '🚨' : '⚠️'}
              </span>
              <span className="alert-message">{alert.message}</span>
              {alert.action === 'upgrade_plan' && (
                <button 
                  id={`billing-alert-upgrade-${index}`}
                  onClick={handleUpgradePlan} 
                  className="billing-alert-action-btn"
                >
                  {t('billingPage.upgrade.button')}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Navigation des onglets */}
      <div className="billing-tabs">
        <button 
          id="billing-tab-overview"
          className={`billing-tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 {t('billingPage.tabs.overview')}
        </button>
        <button 
          id="billing-tab-usage"
          className={`billing-tab-button ${activeTab === 'usage' ? 'active' : ''}`}
          onClick={() => setActiveTab('usage')}
        >
          📈 {t('billingPage.tabs.usage')}
        </button>
        <button 
          id="billing-tab-history"
          className={`billing-tab-button ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          📜 {t('billingPage.tabs.history')}
        </button>
        <button 
          id="billing-tab-plans"
          className={`billing-tab-button ${activeTab === 'plans' ? 'active' : ''}`}
          onClick={() => setActiveTab('plans')}
        >
          💎 {t('billingPage.tabs.plans')}
        </button>
      </div>

      {/* Contenu des onglets */}
      <div className="billing-content">
        
        {/* Onglet Vue d'ensemble */}
        {activeTab === 'overview' && (
          <div className="billing-overview">
            <div className="overview-cards">
              
              {/* Carte Plan actuel */}
              <div className="overview-card current-plan">
                <div className="card-header">
                  <h3>🎫 {t('billingPage.overview.currentPlan')}</h3>
                </div>
                <div className="card-content">
                  <div className="plan-info">
                    <div className="plan-name">{getPlanDisplayName(license.plan)}</div>
                    <div className="plan-type">{license.type === 'trial' ? t('billingPage.trial') : t('billingPage.subscription')}</div>
                    {license.endDate && (
                      <div className="plan-expiry">
                        {t('billingPage.expiresOn')}: {new Date(license.endDate).toLocaleDateString()}
                        {license.daysRemaining && (
                          <span className="days-remaining"> ({license.daysRemaining} jours)</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="plan-actions">
                    <button 
                      id="billing-main-upgrade-button"
                      onClick={handleUpgradePlan} 
                      className="billing-upgrade-button"
                    >
                      {t('billingPage.upgrade.button')}
                    </button>
                  </div>
                </div>
              </div>

              {/* Carte Usage rapide */}
              <div className="overview-card usage-summary">
                <div className="card-header">
                  <h3>📊 {t('billingPage.overview.usageSummary')}</h3>
                </div>
                <div className="card-content">
                  <div className="usage-items">
                    <div className="usage-item">
                      <span className="usage-label">🔌 API Calls</span>
                      <span className="usage-value">
                        {usage.apiCalls?.used || 0} / {limits.maxApiCalls || 0}
                      </span>
                      <div className={`usage-bar ${getUsageStatus(formatUsagePercentage(usage.apiCalls?.used || 0, limits.maxApiCalls))}`}>
                        <div 
                          className="usage-fill"
                          style={{ width: `${formatUsagePercentage(usage.apiCalls?.used || 0, limits.maxApiCalls)}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div className="usage-item">
                      <span className="usage-label">🔗 Nœuds</span>
                      <span className="usage-value">
                        {usage.nodes?.used || 0} / {limits.maxNodes || 0}
                      </span>
                      <div className={`usage-bar ${getUsageStatus(formatUsagePercentage(usage.nodes?.used || 0, limits.maxNodes))}`}>
                        <div 
                          className="usage-fill"
                          style={{ width: `${formatUsagePercentage(usage.nodes?.used || 0, limits.maxNodes)}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Carte Actions rapides */}
              <div className="overview-card quick-actions">
                <div className="card-header">
                  <h3>⚡ {t('billingPage.overview.quickActions')}</h3>
                </div>
                <div className="card-content">
                  <div className="action-buttons">
                    <button 
                      id="billing-overview-upgrade-button"
                      onClick={handleUpgradePlan} 
                      className="billing-action-button primary"
                    >
                      ⬆️ {t('billingPage.actions.upgrade')}
                    </button>
                    <button 
                      id="billing-overview-usage-button"
                      onClick={() => setActiveTab('usage')} 
                      className="billing-action-button"
                    >
                      📈 {t('billingPage.actions.viewUsage')}
                    </button>
                    <button 
                      id="billing-overview-history-button"
                      onClick={() => setActiveTab('history')} 
                      className="billing-action-button"
                    >
                      📜 {t('billingPage.actions.viewHistory')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Onglet Usage détaillé */}
        {activeTab === 'usage' && (
          <div className="billing-usage">
            <div className="usage-header">
              <h3>📈 {t('billingPage.usage.title')}</h3>
              <button 
                id="billing-usage-refresh-button"
                onClick={loadBillingData} 
                className="billing-refresh-button"
              >
                🔄 {t('common.refresh')}
              </button>
            </div>
            
            <div className="usage-grid">
              {/* API Calls */}
              <div className="usage-card">
                <div className="usage-card-header">
                  <div className="usage-icon">🔌</div>
                  <div className="usage-title">
                    <h4>API Calls</h4>
                    <span className="usage-subtitle">{t('billingPage.usage.apiCallsDesc')}</span>
                  </div>
                  <div className={`usage-percentage ${getUsageStatus(formatUsagePercentage(usage.apiCalls?.used || 0, limits.maxApiCalls))}`}>
                    {formatUsagePercentage(usage.apiCalls?.used || 0, limits.maxApiCalls)}%
                  </div>
                </div>
                <div className="usage-progress">
                  <div className={`progress-bar ${getUsageStatus(formatUsagePercentage(usage.apiCalls?.used || 0, limits.maxApiCalls))}`}>
                    <div 
                      className="progress-fill"
                      style={{ width: `${formatUsagePercentage(usage.apiCalls?.used || 0, limits.maxApiCalls)}%` }}
                    ></div>
                  </div>
                  <div className="usage-numbers">
                    <span className="used">{(usage.apiCalls?.used || 0).toLocaleString()}</span>
                    <span className="separator">/</span>
                    <span className="limit">{(limits.maxApiCalls || 0).toLocaleString()}</span>
                  </div>
                </div>
                <div className="usage-remaining">
                  {(usage.apiCalls?.remaining || 0).toLocaleString()} {t('billingPage.usage.remaining')}
                </div>
              </div>

              {/* Nœuds */}
              <div className="usage-card">
                <div className="usage-card-header">
                  <div className="usage-icon">🔗</div>
                  <div className="usage-title">
                    <h4>{t('billingPage.usage.nodes')}</h4>
                    <span className="usage-subtitle">{t('billingPage.usage.nodesDesc')}</span>
                  </div>
                  <div className={`usage-percentage ${getUsageStatus(formatUsagePercentage(usage.nodes?.used || 0, limits.maxNodes))}`}>
                    {formatUsagePercentage(usage.nodes?.used || 0, limits.maxNodes)}%
                  </div>
                </div>
                <div className="usage-progress">
                  <div className={`progress-bar ${getUsageStatus(formatUsagePercentage(usage.nodes?.used || 0, limits.maxNodes))}`}>
                    <div 
                      className="progress-fill"
                      style={{ width: `${formatUsagePercentage(usage.nodes?.used || 0, limits.maxNodes)}%` }}
                    ></div>
                  </div>
                  <div className="usage-numbers">
                    <span className="used">{(usage.nodes?.used || 0).toLocaleString()}</span>
                    <span className="separator">/</span>
                    <span className="limit">{(limits.maxNodes || 0).toLocaleString()}</span>
                  </div>
                </div>
                <div className="usage-remaining">
                  {(usage.nodes?.remaining || 0).toLocaleString()} {t('billingPage.usage.remaining')}
                </div>
              </div>

              {/* Stockage */}
              <div className="usage-card">
                <div className="usage-card-header">
                  <div className="usage-icon">💾</div>
                  <div className="usage-title">
                    <h4>{t('billingPage.usage.storage')}</h4>
                    <span className="usage-subtitle">{t('billingPage.usage.storageDesc')}</span>
                  </div>
                  <div className={`usage-percentage ${getUsageStatus(formatUsagePercentage(usage.storage?.used || 0, limits.maxStorage))}`}>
                    {formatUsagePercentage(usage.storage?.used || 0, limits.maxStorage)}%
                  </div>
                </div>
                <div className="usage-progress">
                  <div className={`progress-bar ${getUsageStatus(formatUsagePercentage(usage.storage?.used || 0, limits.maxStorage))}`}>
                    <div 
                      className="progress-fill"
                      style={{ width: `${formatUsagePercentage(usage.storage?.used || 0, limits.maxStorage)}%` }}
                    ></div>
                  </div>
                  <div className="usage-numbers">
                    <span className="used">{(usage.storage?.used || 0).toFixed(2)} GB</span>
                    <span className="separator">/</span>
                    <span className="limit">{(limits.maxStorage || 0).toFixed(1)} GB</span>
                  </div>
                </div>
                <div className="usage-remaining">
                  {(usage.storage?.remaining || 0).toFixed(2)} GB {t('billingPage.usage.remaining')}
                </div>
              </div>

              {/* Utilisateurs */}
              <div className="usage-card">
                <div className="usage-card-header">
                  <div className="usage-icon">👥</div>
                  <div className="usage-title">
                    <h4>{t('billingPage.usage.users')}</h4>
                    <span className="usage-subtitle">{t('billingPage.usage.usersDesc')}</span>
                  </div>
                  <div className={`usage-percentage ${getUsageStatus(formatUsagePercentage(usage.users?.used || 1, limits.maxUsers))}`}>
                    {formatUsagePercentage(usage.users?.used || 1, limits.maxUsers)}%
                  </div>
                </div>
                <div className="usage-progress">
                  <div className={`progress-bar ${getUsageStatus(formatUsagePercentage(usage.users?.used || 1, limits.maxUsers))}`}>
                    <div 
                      className="progress-fill"
                      style={{ width: `${formatUsagePercentage(usage.users?.used || 1, limits.maxUsers)}%` }}
                    ></div>
                  </div>
                  <div className="usage-numbers">
                    <span className="used">{usage.users?.used || 1}</span>
                    <span className="separator">/</span>
                    <span className="limit">{limits.maxUsers || 1}</span>
                  </div>
                </div>
                <div className="usage-remaining">
                  {(usage.users?.remaining || 0)} {t('billingPage.usage.spotsRemaining')}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Onglet Historique */}
        {activeTab === 'history' && (
          <div className="billing-history">
            <h3>📜 {t('billingPage.history.title')}</h3>
            
            {paymentHistory.length === 0 && invoices.length === 0 ? (
              <div className="empty-history">
                <div className="empty-icon">📄</div>
                <h4>{t('billingPage.history.empty.title')}</h4>
                <p>{t('billingPage.history.empty.description')}</p>
                <p className="no-data-message">{t('billingPage.history.empty.noData')}</p>
              </div>
            ) : (
              <div className="history-content">
                {/* Historique des paiements */}
                {paymentHistory.length > 0 ? (
                  <div className="payments-section">
                    <h4>💳 {t('billingPage.history.payments')}</h4>
                    <div className="payments-list">
                      {paymentHistory.map((payment, index) => (
                        <div key={index} className="payment-item">
                          <div className="payment-info">
                            <span className="payment-date">
                              {payment.date ? new Date(payment.date).toLocaleDateString() : t('billingPage.history.empty.noData')}
                            </span>
                            <span className="payment-description">{payment.description || t('billingPage.history.empty.noData')}</span>
                          </div>
                          <div className="payment-amount">
                            {payment.amount ? `${payment.amount}€` : t('billingPage.history.empty.noData')}
                          </div>
                          <div className={`payment-status ${payment.status || 'unknown'}`}>
                            {payment.status ? t(`billingPage.history.status.${payment.status}`) : t('billingPage.history.empty.noData')}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="no-payments">
                    <p>💳 {t('billingPage.history.empty.noData')} - {t('billingPage.history.payments')}</p>
                  </div>
                )}

                {/* Factures */}
                {invoices.length > 0 ? (
                  <div className="invoices-section">
                    <h4>📄 {t('billingPage.history.invoices')}</h4>
                    <div className="invoices-list">
                      {invoices.map((invoice, index) => (
                        <div key={index} className="invoice-item">
                          <div className="invoice-info">
                            <span className="invoice-number">#{invoice.number || t('billingPage.history.empty.noData')}</span>
                            <span className="invoice-date">
                              {invoice.date ? new Date(invoice.date).toLocaleDateString() : t('billingPage.history.empty.noData')}
                            </span>
                          </div>
                          <div className="invoice-amount">{invoice.amount ? `${invoice.amount}€` : t('billingPage.history.empty.noData')}</div>
                          <button 
                            id={`billing-invoice-download-${invoice.id || index}`}
                            onClick={() => handleDownloadInvoice(invoice.id)}
                            className="billing-download-button"
                            disabled={!invoice.id}
                          >
                            📥 {t('billingPage.history.download')}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="no-invoices">
                    <p>📄 {t('billingPage.history.empty.noData')} - {t('billingPage.history.invoices')}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Onglet Plans */}
        {activeTab === 'plans' && (
          <div className="billing-plans">
            <h3>💎 {t('billingPage.plans.title')}</h3>
            <div className="plans-grid">
              {['free', 'starter', 'professional', 'enterprise'].map((plan) => (
                <div 
                  key={plan} 
                  className={`plan-card ${license.plan === plan ? 'current' : ''}`}
                >
                  <div className="plan-header">
                    <h4>{getPlanDisplayName(plan)}</h4>
                    {license.plan === plan && (
                      <span className="current-badge">{t('billingPage.plans.current')}</span>
                    )}
                  </div>
                  <div className="plan-features">
                    {getPlanFeatures(plan).map((feature, index) => (
                      <div key={index} className="plan-feature">
                        ✓ {feature.value}
                      </div>
                    ))}
                  </div>
                  <div className="plan-footer">
                    {license.plan !== plan && (
                      <button 
                        id={`billing-plan-select-${plan}`}
                        onClick={handleUpgradePlan}
                        className="billing-select-plan-button"
                      >
                        {plan === 'free' ? t('billingPage.plans.downgrade') : t('billingPage.plans.upgrade')}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingPage;
